import { LucideIcon, Scale, Gavel, Building, Briefcase, Shield, FileText, Landmark, Search, ClipboardList, FileSearch, BookOpen, PenTool, Database, AlertTriangle, Scroll, FileCheck, Hammer, Receipt, Clock } from 'lucide-react'

export interface ShortcutSubcategory {
  id: string
  label: string                    // Polish display name
  promptTemplate: string           // AI prompt to pre-fill in query field
  docType?: string                 // Maps to DOC_TYPE_OPTIONS (e.g., 'legal_opinion')
  role?: string                    // Maps to ROLE_OPTIONS (e.g., 'defender_civil')
  instructions?: string            // Additional instructions to pre-fill
}

export interface ShortcutCategory {
  id: string
  label: string                    // e.g., "Proces cywilny i gospodarczy"
  icon: LucideIcon
  color: string                    // Tailwind color key
  subcategories: ShortcutSubcategory[]
}

export interface ShortcutSection {
  id: string
  label: string
  categories: ShortcutCategory[]
}

// ============================================================================
// SECTION 1: GENEROWANIE DOKUMENTÓW
// ============================================================================

const DOCUMENT_GENERATION_CATEGORIES: ShortcutCategory[] = [
  {
    id: 'civil_commercial',
    label: 'Proces cywilny i gospodarczy',
    icon: Scale,
    color: 'blue',
    subcategories: [
      {
        id: 'claim',
        label: 'Pozew',
        promptTemplate: 'Przygotuj projekt pozwu. Określ podstawę prawną roszczenia, przedstaw stan faktyczny i uzasadnienie.',
        docType: 'claim',
        role: 'plaintiff_rep',
        instructions: 'Wskaż dowody na poparcie roszczenia. Określ wartość przedmiotu sporu.',
      },
      {
        id: 'response_to_claim',
        label: 'Odpowiedź na pozew',
        promptTemplate: 'Przygotuj projekt odpowiedzi na pozew. Przeanalizuj roszczenia powoda i przedstaw argumenty obrony pozwanego.',
        docType: 'response_to_claim',
        role: 'defender_civil',
        instructions: 'Skup się na zarzutach procesowych i materialnych. Wskaż brak podstaw prawnych roszczenia.',
      },
      {
        id: 'objection_payment_order',
        label: 'Sprzeciw/Zarzuty od nakazu zapłaty',
        promptTemplate: 'Przygotuj sprzeciw lub zarzuty od nakazu zapłaty (EPU/upominawcze/nakazowe). Podważ zasadność roszczenia i przedstaw zarzuty.',
        docType: 'response_to_claim',
        role: 'defender_civil',
        instructions: 'Uwzględnij terminy procesowe. Wskaż na braki formalne pozwu lub nakazu.',
      },
      {
        id: 'motions_pending',
        label: 'Wnioski w toku',
        promptTemplate: 'Przygotuj wniosek procesowy (dowodowy, o przywrócenie terminu, zwolnienie od kosztów, ustanowienie pełnomocnika z urzędu).',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Wskaż podstawę prawną wniosku i uzasadnienie.',
      },
      {
        id: 'interim_measures',
        label: 'Zabezpieczenie roszczenia',
        promptTemplate: 'Przygotuj wniosek o zabezpieczenie roszczenia (także przed wszczęciem postępowania). Uzasadnij interes prawny i uprawdopodobnij roszczenie.',
        docType: 'claim',
        role: 'plaintiff_rep',
        instructions: 'Wskaż sposób zabezpieczenia i jego proporcjonalność.',
      },
      {
        id: 'appeal_complaint',
        label: 'Apelacja / Zażalenie',
        promptTemplate: 'Przygotuj projekt apelacji lub zażalenia. Wskaż zarzuty i ich uzasadnienie.',
        docType: 'appeal',
        role: 'neutral',
        instructions: 'Skup się na naruszeniach prawa materialnego i procesowego. Wskaż zakres zaskarżenia.',
      },
      {
        id: 'settlement',
        label: 'Ugoda (sądowa/pozasądowa)',
        promptTemplate: 'Przygotuj projekt ugody sądowej lub pozasądowej. Określ warunki porozumienia stron.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Uwzględnij wzajemne ustępstwa i sposób wykonania ugody.',
      },
      {
        id: 'pre_litigation_demands',
        label: 'Wezwania przedsądowe',
        promptTemplate: 'Przygotuj wezwanie przedsądowe do zapłaty lub zaniechania naruszenia.',
        docType: 'memo',
        role: 'plaintiff_rep',
        instructions: 'Wskaż termin na spełnienie żądania i konsekwencje braku reakcji.',
      },
      {
        id: 'termination_statements',
        label: 'Oświadczenia: odstąpienie / wypowiedzenie / rozwiązanie',
        promptTemplate: 'Przygotuj oświadczenie o odstąpieniu od umowy, wypowiedzeniu lub rozwiązaniu stosunku prawnego.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Wskaż podstawę prawną i skutki oświadczenia.',
      },
      {
        id: 'reply_rejoinder',
        label: 'Replika / Duplika / dalsze pisma',
        promptTemplate: 'Przygotuj replikę, duplikę lub dalsze pismo procesowe w odpowiedzi na stanowisko strony przeciwnej.',
        docType: 'response_to_claim',
        role: 'neutral',
        instructions: 'Odnieść się do argumentów strony przeciwnej punkt po punkcie.',
      },
      {
        id: 'civil_other',
        label: 'INNE',
        promptTemplate: 'Przygotuj dokument z zakresu procesu cywilnego lub gospodarczego.',
        docType: 'memo',
        role: 'neutral',
      },
    ],
  },
  {
    id: 'labor_hr',
    label: 'Prawo pracy i HR',
    icon: Briefcase,
    color: 'green',
    subcategories: [
      {
        id: 'employment_contract',
        label: 'Umowa o pracę',
        promptTemplate: 'Przygotuj umowę o pracę (warianty, aneks, kontrakt menedżerski). Określ warunki zatrudnienia.',
        docType: 'memo',
        role: 'business_advisor',
        instructions: 'Uwzględnij przepisy Kodeksu pracy dotyczące treści umowy.',
      },
      {
        id: 'termination_employment',
        label: 'Wypowiedzenie / rozwiązanie / porozumienie stron',
        promptTemplate: 'Przygotuj dokument wypowiedzenia umowy o pracę, rozwiązania lub porozumienia stron.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Wskaż podstawę prawną i okresy wypowiedzenia.',
      },
      {
        id: 'hr_regulations',
        label: 'Regulamin i polityki HR',
        promptTemplate: 'Przygotuj regulamin pracy, wynagradzania lub polityki wewnętrzne HR.',
        docType: 'memo',
        role: 'business_advisor',
        instructions: 'Uwzględnij wymogi Kodeksu pracy i RODO.',
      },
      {
        id: 'labor_court_appeal',
        label: 'Odwołanie do sądu pracy',
        promptTemplate: 'Przygotuj odwołanie od wypowiedzenia umowy o pracę do sądu pracy. Wskaż na nieprawidłowości.',
        docType: 'claim',
        role: 'plaintiff_rep',
        instructions: 'Sprawdź zgodność z art. 30, 38, 45 Kodeksu pracy.',
      },
      {
        id: 'dispute_letters',
        label: 'Pisma sporne (mobbing/dyskryminacja/compliance)',
        promptTemplate: 'Przygotuj pismo w sprawie mobbingu, dyskryminacji lub naruszenia compliance.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Uwzględnij przepisy antydyskryminacyjne i procedury wewnętrzne.',
      },
      {
        id: 'work_certificate',
        label: 'Świadectwo pracy / sprostowanie',
        promptTemplate: 'Przygotuj świadectwo pracy lub wniosek o jego sprostowanie.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Uwzględnij wymogi formalne świadectwa pracy.',
      },
      {
        id: 'non_compete',
        label: 'Zakaz konkurencji',
        promptTemplate: 'Przygotuj umowę o zakazie konkurencji (w trakcie lub po ustaniu stosunku pracy).',
        docType: 'memo',
        role: 'business_advisor',
        instructions: 'Określ zakres, czas trwania i odszkodowanie.',
      },
      {
        id: 'labor_other',
        label: 'INNE',
        promptTemplate: 'Przygotuj dokument z zakresu prawa pracy i HR.',
        docType: 'memo',
        role: 'neutral',
      },
    ],
  },
  {
    id: 'corporate_transactions',
    label: 'Spółki, korporacyjne, Transakcje (KSH)',
    icon: Building,
    color: 'purple',
    subcategories: [
      {
        id: 'company_agreement',
        label: 'Umowa/statut spółki + aneksy',
        promptTemplate: 'Przygotuj umowę spółki, statut lub aneks do dokumentów korporacyjnych.',
        docType: 'memo',
        role: 'business_advisor',
        instructions: 'Uwzględnij przepisy KSH i specyfikę spółki.',
      },
      {
        id: 'resolutions_protocols',
        label: 'Uchwały i protokoły',
        promptTemplate: 'Przygotuj uchwałę lub protokół (zarządu/WZ/RN).',
        docType: 'memo',
        role: 'business_advisor',
        instructions: 'Uwzględnij wymogi formalne i kworum.',
      },
      {
        id: 'powers_of_attorney',
        label: 'Pełnomocnictwa',
        promptTemplate: 'Przygotuj pełnomocnictwo (procesowe/korporacyjne/szczególne).',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Określ zakres umocowania i wymogi formalne.',
      },
      {
        id: 'nda',
        label: 'NDA',
        promptTemplate: 'Przygotuj umowę o zachowaniu poufności (NDA).',
        docType: 'memo',
        role: 'business_advisor',
        instructions: 'Określ zakres informacji poufnych i sankcje.',
      },
      {
        id: 'shareholders_agreement',
        label: 'Shareholders\' Agreement',
        promptTemplate: 'Przygotuj umowę wspólników (shareholders\' agreement).',
        docType: 'memo',
        role: 'business_advisor',
        instructions: 'Uwzględnij prawa i obowiązki wspólników, tag-along, drag-along.',
      },
      {
        id: 'transaction_docs',
        label: 'Dokumenty transakcyjne',
        promptTemplate: 'Przygotuj dokument transakcyjny (LOI/MoU/term sheet/szkic SPA/APA/TSA).',
        docType: 'memo',
        role: 'business_advisor',
        instructions: 'Określ kluczowe warunki transakcji.',
      },
      {
        id: 'due_diligence',
        label: 'Due diligence',
        promptTemplate: 'Przygotuj raport due diligence lub checklistę ryzyk.',
        docType: 'legal_opinion',
        role: 'business_advisor',
        instructions: 'Zidentyfikuj kluczowe ryzyka prawne i biznesowe.',
      },
      {
        id: 'krs_documents',
        label: 'KRS: pisma i załączniki',
        promptTemplate: 'Przygotuj pisma lub załączniki do wniosku KRS.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Uwzględnij wymogi formalne rejestru.',
      },
      {
        id: 'restructuring',
        label: 'Restrukturyzacje',
        promptTemplate: 'Przygotuj dokumenty restrukturyzacyjne (łączenie/podział/przekształcenie spółki).',
        docType: 'memo',
        role: 'business_advisor',
        instructions: 'Uwzględnij procedury KSH i skutki podatkowe.',
      },
      {
        id: 'corporate_other',
        label: 'INNE',
        promptTemplate: 'Przygotuj dokument z zakresu prawa spółek i transakcji.',
        docType: 'memo',
        role: 'business_advisor',
      },
    ],
  },
  {
    id: 'administrative',
    label: 'Administracyjne i sądowoadministracyjne',
    icon: Landmark,
    color: 'indigo',
    subcategories: [
      {
        id: 'admin_appeal',
        label: 'Odwołanie od decyzji',
        promptTemplate: 'Przygotuj odwołanie od decyzji administracyjnej do organu wyższego stopnia.',
        docType: 'appeal',
        role: 'neutral',
        instructions: 'Wskaż na uchybienia proceduralne i błędną interpretację przepisów.',
      },
      {
        id: 'admin_complaint',
        label: 'Zażalenie',
        promptTemplate: 'Przygotuj zażalenie na postanowienie organu administracyjnego.',
        docType: 'appeal',
        role: 'neutral',
        instructions: 'Wskaż naruszone przepisy i zakres zaskarżenia.',
      },
      {
        id: 'wsa_complaint',
        label: 'Skarga do WSA',
        promptTemplate: 'Przygotuj skargę do Wojewódzkiego Sądu Administracyjnego na decyzję administracyjną.',
        docType: 'cassation',
        role: 'neutral',
        instructions: 'Wskaż na naruszenie przepisów postępowania i prawa materialnego.',
      },
      {
        id: 'nsa_cassation',
        label: 'Skarga kasacyjna do NSA',
        promptTemplate: 'Przygotuj szkic skargi kasacyjnej do Naczelnego Sądu Administracyjnego.',
        docType: 'cassation',
        role: 'neutral',
        instructions: 'Wskaż podstawy kasacyjne i zakres zaskarżenia.',
      },
      {
        id: 'admin_applications',
        label: 'Wnioski administracyjne',
        promptTemplate: 'Przygotuj wniosek administracyjny o zezwolenie, koncesję lub zaświadczenie.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Uwzględnij wymogi formalne i dokumenty załącznikowe.',
      },
      {
        id: 'tax_rulings',
        label: 'Interpretacje indywidualne',
        promptTemplate: 'Przygotuj wniosek o wydanie interpretacji indywidualnej przepisów prawa.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Szczegółowo opisz stan faktyczny i własne stanowisko.',
      },
      {
        id: 'clarification_letters',
        label: 'Pisma wyjaśniające i uzupełniające',
        promptTemplate: 'Przygotuj pismo wyjaśniające lub uzupełniające w postępowaniu administracyjnym.',
        docType: 'memo',
        role: 'neutral',
      },
      {
        id: 'evidence_motions_admin',
        label: 'Wnioski dowodowe',
        promptTemplate: 'Przygotuj wniosek dowodowy w postępowaniu administracyjnym.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Wskaż fakty do udowodnienia i środki dowodowe.',
      },
      {
        id: 'suspension_execution',
        label: 'Wstrzymanie wykonania decyzji',
        promptTemplate: 'Przygotuj wniosek o wstrzymanie wykonania decyzji administracyjnej.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Uzasadnij niebezpieczeństwo wyrządzenia znacznej szkody.',
      },
      {
        id: 'admin_other',
        label: 'INNE',
        promptTemplate: 'Przygotuj dokument z zakresu prawa administracyjnego.',
        docType: 'memo',
        role: 'neutral',
      },
    ],
  },
  {
    id: 'criminal',
    label: 'Karne i karne skarbowe',
    icon: Gavel,
    color: 'red',
    subcategories: [
      {
        id: 'crime_notification',
        label: 'Zawiadomienie o podejrzeniu przestępstwa',
        promptTemplate: 'Przygotuj zawiadomienie o podejrzeniu popełnienia przestępstwa.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Szczegółowo opisz okoliczności i wskaż dowody.',
      },
      {
        id: 'subsidiary_indictment',
        label: 'Subsydiarny akt oskarżenia',
        promptTemplate: 'Przygotuj szkic subsydiarnego aktu oskarżenia.',
        docType: 'claim',
        role: 'plaintiff_rep',
        instructions: 'Uwzględnij wymogi formalne aktu oskarżenia.',
      },
      {
        id: 'response_to_indictment',
        label: 'Odpowiedź na akt oskarżenia',
        promptTemplate: 'Przygotuj odpowiedź na akt oskarżenia. Wskaż argumenty obrony.',
        docType: 'response_to_claim',
        role: 'defender_criminal',
        instructions: 'Przeanalizuj zarzuty i wskaż linię obrony.',
      },
      {
        id: 'criminal_appeal',
        label: 'Apelacja / Zażalenie',
        promptTemplate: 'Przygotuj apelację lub zażalenie w sprawie karnej.',
        docType: 'appeal',
        role: 'defender_criminal',
        instructions: 'Wskaż zarzuty i zakres zaskarżenia.',
      },
      {
        id: 'procedural_motions',
        label: 'Wnioski procesowe',
        promptTemplate: 'Przygotuj wniosek procesowy (o umorzenie, zmianę środka zapobiegawczego).',
        docType: 'memo',
        role: 'defender_criminal',
        instructions: 'Uzasadnij wniosek przepisami i okolicznościami sprawy.',
      },
      {
        id: 'evidence_motions_criminal',
        label: 'Wnioski dowodowe',
        promptTemplate: 'Przygotuj wniosek dowodowy w postępowaniu karnym.',
        docType: 'memo',
        role: 'defender_criminal',
        instructions: 'Wskaż okoliczności do udowodnienia i środki dowodowe.',
      },
      {
        id: 'voluntary_submission',
        label: 'Dobrowolne poddanie się odpowiedzialności (KKS)',
        promptTemplate: 'Przygotuj szkic wniosku o dobrowolne poddanie się odpowiedzialności w sprawie karnej skarbowej.',
        docType: 'memo',
        role: 'defender_criminal',
        instructions: 'Uwzględnij warunki z art. 17 KKS.',
      },
      {
        id: 'criminal_other',
        label: 'INNE',
        promptTemplate: 'Przygotuj dokument z zakresu prawa karnego lub karnego skarbowego.',
        docType: 'memo',
        role: 'defender_criminal',
      },
    ],
  },
  {
    id: 'enforcement',
    label: 'Egzekucja i zabezpieczenie',
    icon: Hammer,
    color: 'orange',
    subcategories: [
      {
        id: 'enforcement_application',
        label: 'Wniosek egzekucyjny',
        promptTemplate: 'Przygotuj wniosek o wszczęcie egzekucji.',
        docType: 'memo',
        role: 'plaintiff_rep',
        instructions: 'Wskaż tytuł wykonawczy i sposób egzekucji.',
      },
      {
        id: 'enforcement_limitation',
        label: 'Ograniczenie / umorzenie egzekucji',
        promptTemplate: 'Przygotuj wniosek o ograniczenie lub umorzenie egzekucji.',
        docType: 'memo',
        role: 'defender_civil',
        instructions: 'Uzasadnij podstawy ograniczenia lub umorzenia.',
      },
      {
        id: 'bailiff_complaint',
        label: 'Skarga na czynności komornika',
        promptTemplate: 'Przygotuj skargę na czynności komornika.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Wskaż naruszone przepisy i zakres zaskarżenia.',
      },
      {
        id: 'enforcement_titles',
        label: 'Pisma dot. tytułów wykonawczych',
        promptTemplate: 'Przygotuj pismo dotyczące tytułów wykonawczych lub wykonania zabezpieczenia.',
        docType: 'memo',
        role: 'neutral',
      },
      {
        id: 'security_execution',
        label: 'Wykonanie zabezpieczenia',
        promptTemplate: 'Przygotuj wniosek o wykonanie zabezpieczenia.',
        docType: 'memo',
        role: 'plaintiff_rep',
        instructions: 'Wskaż sposób wykonania zabezpieczenia.',
      },
      {
        id: 'enforcement_objections',
        label: 'Zarzuty w egzekucji',
        promptTemplate: 'Przygotuj zarzuty w postępowaniu egzekucyjnym.',
        docType: 'memo',
        role: 'defender_civil',
        instructions: 'Wskaż podstawy zarzutów przeciw egzekucji.',
      },
      {
        id: 'payment_plan',
        label: 'Plan spłaty / ugoda egzekucyjna',
        promptTemplate: 'Przygotuj plan spłaty lub ugodę egzekucyjną.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Określ warunki spłaty i harmonogram.',
      },
      {
        id: 'enforcement_other',
        label: 'INNE',
        promptTemplate: 'Przygotuj dokument z zakresu egzekucji i zabezpieczenia.',
        docType: 'memo',
        role: 'neutral',
      },
    ],
  },
  {
    id: 'public_procurement',
    label: 'Zamówienia publiczne',
    icon: ClipboardList,
    color: 'teal',
    subcategories: [
      {
        id: 'kio_appeal',
        label: 'Odwołanie do KIO',
        promptTemplate: 'Przygotuj odwołanie do Krajowej Izby Odwoławczej.',
        docType: 'appeal',
        role: 'neutral',
        instructions: 'Wskaż naruszone przepisy PZP i interes prawny.',
      },
      {
        id: 'kio_joinder',
        label: 'Przystąpienie do postępowania odwoławczego',
        promptTemplate: 'Przygotuj zgłoszenie przystąpienia do postępowania odwoławczego przed KIO.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Wskaż interes w rozstrzygnięciu i stanowisko.',
      },
      {
        id: 'response_to_appeal',
        label: 'Odpowiedź na odwołanie',
        promptTemplate: 'Przygotuj odpowiedź na odwołanie przed KIO.',
        docType: 'response_to_claim',
        role: 'neutral',
        instructions: 'Odnieść się do zarzutów odwołania.',
      },
      {
        id: 'court_complaint_pzp',
        label: 'Skarga do sądu',
        promptTemplate: 'Przygotuj skargę do sądu na orzeczenie KIO.',
        docType: 'cassation',
        role: 'neutral',
        instructions: 'Wskaż podstawy skargi i zakres zaskarżenia.',
      },
      {
        id: 'abnormally_low_price',
        label: 'Wyjaśnienia rażąco niskiej ceny',
        promptTemplate: 'Przygotuj wyjaśnienia dotyczące rażąco niskiej ceny w zamówieniu publicznym.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Uzasadnij kalkulację ceny i jej realność.',
      },
      {
        id: 'swz_analysis',
        label: 'Analiza SWZ / OPZ',
        promptTemplate: 'Przeanalizuj Specyfikację Warunków Zamówienia lub Opis Przedmiotu Zamówienia.',
        docType: 'legal_opinion',
        role: 'neutral',
        instructions: 'Wskaż ryzyka i niejasności w dokumentacji.',
      },
      {
        id: 'procurement_other',
        label: 'INNE',
        promptTemplate: 'Przygotuj dokument z zakresu zamówień publicznych.',
        docType: 'memo',
        role: 'neutral',
      },
    ],
  },
  {
    id: 'civil_contracts',
    label: 'Umowy cywilne i gospodarcze',
    icon: FileText,
    color: 'sky',
    subcategories: [
      {
        id: 'service_agreement',
        label: 'Umowa usług / B2B',
        promptTemplate: 'Przygotuj umowę o świadczenie usług lub współpracy B2B.',
        docType: 'memo',
        role: 'business_advisor',
        instructions: 'Określ zakres usług, wynagrodzenie i odpowiedzialność.',
      },
      {
        id: 'sale_delivery',
        label: 'Umowa sprzedaży/dostawy',
        promptTemplate: 'Przygotuj umowę sprzedaży lub dostawy towarów.',
        docType: 'memo',
        role: 'business_advisor',
        instructions: 'Określ przedmiot, cenę, warunki dostawy i reklamacji.',
      },
      {
        id: 'lease_rental',
        label: 'Umowa najmu/dzierżawy',
        promptTemplate: 'Przygotuj umowę najmu lub dzierżawy.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Określ przedmiot, czynsz, okres i warunki rozwiązania.',
      },
      {
        id: 'work_mandate',
        label: 'Umowa o dzieło/zlecenie',
        promptTemplate: 'Przygotuj umowę o dzieło lub zlecenie.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Określ przedmiot, wynagrodzenie i terminy.',
      },
      {
        id: 'it_contracts',
        label: 'Umowy IT',
        promptTemplate: 'Przygotuj umowę IT (wdrożeniowa / SLA / utrzymaniowa).',
        docType: 'memo',
        role: 'business_advisor',
        instructions: 'Określ zakres, SLA, prawa własności intelektualnej.',
      },
      {
        id: 'other_contracts',
        label: 'Inne umowy',
        promptTemplate: 'Przygotuj inne umowy cywilne lub gospodarcze (nazwane/nienazwane).',
        docType: 'memo',
        role: 'neutral',
      },
    ],
  },
  {
    id: 'tax',
    label: 'Podatki',
    icon: Receipt,
    color: 'emerald',
    subcategories: [
      {
        id: 'tax_opinion',
        label: 'Opinia podatkowa',
        promptTemplate: 'Przygotuj opinię podatkową analizującą skutki podatkowe transakcji lub zdarzenia.',
        docType: 'legal_opinion',
        role: 'business_advisor',
        instructions: 'Uwzględnij przepisy podatkowe i interpretacje organów.',
      },
      {
        id: 'tax_ruling_request',
        label: 'Wniosek o interpretację indywidualną',
        promptTemplate: 'Przygotuj wniosek o wydanie interpretacji indywidualnej przepisów podatkowych.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Szczegółowo opisz stan faktyczny i własne stanowisko.',
      },
      {
        id: 'tax_appeals',
        label: 'Odwołania/zażalenia podatkowe',
        promptTemplate: 'Przygotuj odwołanie lub zażalenie w sprawie podatkowej.',
        docType: 'appeal',
        role: 'neutral',
        instructions: 'Wskaż naruszone przepisy i zakres zaskarżenia.',
      },
      {
        id: 'tax_court_complaints',
        label: 'Skargi do WSA / NSA',
        promptTemplate: 'Przygotuj skargę do sądu administracyjnego w sprawie podatkowej.',
        docType: 'cassation',
        role: 'neutral',
        instructions: 'Wskaż podstawy skargi i naruszone przepisy.',
      },
      {
        id: 'tax_audit_letters',
        label: 'Pisma do kontroli podatkowej',
        promptTemplate: 'Przygotuj pismo w toku kontroli podatkowej lub celno-skarbowej.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Odnieść się do ustaleń kontroli.',
      },
      {
        id: 'tax_analysis',
        label: 'Analiza skutków podatkowych transakcji',
        promptTemplate: 'Przeanalizuj skutki podatkowe planowanej transakcji.',
        docType: 'legal_opinion',
        role: 'business_advisor',
        instructions: 'Uwzględnij wszystkie relewantne podatki (CIT, VAT, PCC).',
      },
      {
        id: 'tax_other',
        label: 'INNE',
        promptTemplate: 'Przygotuj dokument z zakresu prawa podatkowego.',
        docType: 'memo',
        role: 'neutral',
      },
    ],
  },
  {
    id: 'compliance_gdpr',
    label: 'Compliance / RODO / e-commerce',
    icon: Shield,
    color: 'amber',
    subcategories: [
      {
        id: 'privacy_policy',
        label: 'Polityka prywatności / klauzule informacyjne',
        promptTemplate: 'Przygotuj politykę prywatności lub klauzule informacyjne RODO.',
        docType: 'memo',
        role: 'business_advisor',
        instructions: 'Uwzględnij art. 13 i 14 RODO.',
      },
      {
        id: 'dpa',
        label: 'Powierzenie przetwarzania (DPA)',
        promptTemplate: 'Przygotuj umowę powierzenia przetwarzania danych osobowych (DPA).',
        docType: 'memo',
        role: 'business_advisor',
        instructions: 'Uwzględnij wymogi art. 28 RODO.',
      },
      {
        id: 'terms_of_service',
        label: 'Regulamin usług/sklep internetowy',
        promptTemplate: 'Przygotuj regulamin świadczenia usług drogą elektroniczną lub sklepu internetowego.',
        docType: 'memo',
        role: 'business_advisor',
        instructions: 'Uwzględnij ustawę o świadczeniu usług drogą elektroniczną i prawa konsumenta.',
      },
      {
        id: 'internal_policies',
        label: 'Polityki wewnętrzne',
        promptTemplate: 'Przygotuj polityki wewnętrzne (AML, antykorupcyjna, whistleblowing, ESG, bezpieczeństwo informacji).',
        docType: 'memo',
        role: 'business_advisor',
        instructions: 'Uwzględnij wymogi regulacyjne i standardy branżowe.',
      },
      {
        id: 'risk_assessment',
        label: 'Ocena ryzyka / audyt zgodności',
        promptTemplate: 'Przygotuj ocenę ryzyka lub raport z audytu zgodności.',
        docType: 'legal_opinion',
        role: 'business_advisor',
        instructions: 'Zidentyfikuj ryzyka i zaproponuj środki zaradcze.',
      },
      {
        id: 'data_breach',
        label: 'Incydent naruszenia danych',
        promptTemplate: 'Przygotuj analizę prawną naruszenia ochrony danych osobowych. Określ obowiązki i rekomendacje.',
        docType: 'legal_opinion',
        role: 'business_advisor',
        instructions: 'Wskaż na obowiązek zgłoszenia do UODO i powiadomienia osób. Określ potencjalne sankcje.',
      },
      {
        id: 'compliance_other',
        label: 'INNE',
        promptTemplate: 'Przygotuj dokument z zakresu compliance, RODO lub e-commerce.',
        docType: 'memo',
        role: 'business_advisor',
      },
    ],
  },
]

// ============================================================================
// SECTION 2: ANALIZA I REVIEW DOKUMENTÓW
// ============================================================================

const DOCUMENT_ANALYSIS_CATEGORIES: ShortcutCategory[] = [
  {
    id: 'summary_brief',
    label: 'Streszczenie i case brief',
    icon: FileSearch,
    color: 'cyan',
    subcategories: [
      {
        id: 'summary',
        label: 'Streszczenie',
        promptTemplate: 'Przygotuj streszczenie załączonego dokumentu.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Wyodrębnij kluczowe informacje i wnioski.',
      },
      {
        id: 'timeline',
        label: 'Oś czasu',
        promptTemplate: 'Przygotuj oś czasu zdarzeń na podstawie załączonego dokumentu.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Chronologicznie uporządkuj zdarzenia z datami.',
      },
      {
        id: 'key_points',
        label: 'Kluczowe tezy/roszczenia/wnioski',
        promptTemplate: 'Wyodrębnij kluczowe tezy, roszczenia i wnioski z załączonego dokumentu.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Przedstaw w punktach najważniejsze elementy.',
      },
    ],
  },
  {
    id: 'risk_review',
    label: 'Analiza ryzyka, review',
    icon: AlertTriangle,
    color: 'rose',
    subcategories: [
      {
        id: 'contract_review',
        label: 'Review umowy',
        promptTemplate: 'Przeprowadź review załączonej umowy. Zidentyfikuj ryzyka i klauzule wymagające negocjacji.',
        docType: 'legal_opinion',
        role: 'business_advisor',
        instructions: 'Wskaż ryzyka dla każdej strony umowy.',
      },
      {
        id: 'document_review',
        label: 'Review dokumentu',
        promptTemplate: 'Przeprowadź review załączonego dokumentu prawnego. Oceń zgodność i kompletność.',
        docType: 'legal_opinion',
        role: 'neutral',
        instructions: 'Zidentyfikuj braki i ryzyka.',
      },
    ],
  },
  {
    id: 'version_comparison',
    label: 'Porównanie wersji umów / dokumentów',
    icon: FileCheck,
    color: 'violet',
    subcategories: [
      {
        id: 'compare_versions',
        label: 'Wskazanie różnic',
        promptTemplate: 'Porównaj wersje umów lub dokumentów i wskaż różnice.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Wyszczególnij wszystkie zmiany między wersjami.',
      },
    ],
  },
  {
    id: 'formal_defects',
    label: 'Braki formalne',
    icon: Search,
    color: 'pink',
    subcategories: [
      {
        id: 'procedural_defects',
        label: 'Braki formalne (proceduralne)',
        promptTemplate: 'Zidentyfikuj braki formalne i proceduralne w załączonym dokumencie.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Wskaż naruszone wymogi formalne.',
      },
      {
        id: 'evidence_gaps',
        label: 'Luki dowodowe / słabe punkty argumentacji',
        promptTemplate: 'Zidentyfikuj luki dowodowe i słabe punkty argumentacji w załączonym dokumencie.',
        docType: 'legal_opinion',
        role: 'neutral',
        instructions: 'Wskaż obszary wymagające wzmocnienia.',
      },
      {
        id: 'clause_risks',
        label: 'Ryzyka klauzulowe (umowy)',
        promptTemplate: 'Zidentyfikuj ryzykowne klauzule w załączonej umowie.',
        docType: 'legal_opinion',
        role: 'business_advisor',
        instructions: 'Wskaż klauzule niekorzystne lub niestandardowe.',
      },
    ],
  },
  {
    id: 'suggestions',
    label: 'Propozycje poprawek',
    icon: PenTool,
    color: 'fuchsia',
    subcategories: [
      {
        id: 'alternative_wording',
        label: 'Alternatywne brzmienia',
        promptTemplate: 'Zaproponuj alternatywne brzmienia dla klauzul lub zapisów w załączonym dokumencie.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Przedstaw wersje alternatywne z uzasadnieniem.',
      },
      {
        id: 'section_comments',
        label: 'Komentarze sekcja po sekcji',
        promptTemplate: 'Przygotuj komentarze do załączonego dokumentu sekcja po sekcji.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Skomentuj każdą sekcję dokumentu.',
      },
    ],
  },
  {
    id: 'data_extraction',
    label: 'Ekstrakcja danych',
    icon: Database,
    color: 'lime',
    subcategories: [
      {
        id: 'extract_data',
        label: 'Strony / kwoty / terminy / klauzule',
        promptTemplate: 'Wyodrębnij kluczowe dane z załączonego dokumentu: strony, kwoty, terminy, klauzule.',
        docType: 'memo',
        role: 'neutral',
        instructions: 'Przedstaw dane w ustrukturyzowanej formie.',
      },
    ],
  },
  {
    id: 'analysis_other',
    label: 'INNE',
    icon: FileText,
    color: 'gray',
    subcategories: [
      {
        id: 'analysis_other_doc',
        label: 'Inna analiza dokumentu',
        promptTemplate: 'Przeprowadź analizę załączonego dokumentu.',
        docType: 'memo',
        role: 'neutral',
      },
    ],
  },
]

// ============================================================================
// SECTION 3: RESEARCH PRAWNY
// ============================================================================

const LEGAL_RESEARCH_CATEGORIES: ShortcutCategory[] = [
  {
    id: 'research_qa',
    label: 'Research prawny Q&A',
    icon: BookOpen,
    color: 'blue',
    subcategories: [
      {
        id: 'legal_answer',
        label: 'Odpowiedź + podstawy prawne',
        promptTemplate: 'Odpowiedz na pytanie prawne wraz z powołaniem podstaw prawnych (artykuły/jednostki redakcyjne).',
        docType: 'legal_opinion',
        role: 'neutral',
        instructions: 'Wskaż konkretne przepisy i orzecznictwo.',
      },
      {
        id: 'case_law_research',
        label: 'Orzecznictwo (sąd/sygn./data + opis)',
        promptTemplate: 'Wyszukaj relewantne orzecznictwo do zagadnienia prawnego.',
        docType: 'legal_opinion',
        role: 'neutral',
        instructions: 'Podaj sygnaturę, datę, sąd i krótki opis tezy.',
      },
      {
        id: 'source_verification',
        label: 'Informacja o źródłach',
        promptTemplate: 'Przeprowadź research prawny i wskaż, czy odpowiedź jest oparta na źródłach.',
        docType: 'legal_opinion',
        role: 'neutral',
        instructions: 'Wyraźnie oznacz źródła informacji.',
      },
    ],
  },
  {
    id: 'case_law_only',
    label: 'Research prawny Orzecznictwo',
    icon: Scroll,
    color: 'amber',
    subcategories: [
      {
        id: 'find_case_law',
        label: 'Wyszukaj orzecznictwo',
        promptTemplate: 'Wyszukaj orzecznictwo sądowe dotyczące wskazanego zagadnienia prawnego.',
        docType: 'legal_opinion',
        role: 'neutral',
        instructions: 'Wskaż sygnatury, daty i krótkie opisy tez orzeczeń.',
      },
    ],
  },
  {
    id: 'document_research',
    label: 'Research z dokumentu',
    icon: FileSearch,
    color: 'teal',
    subcategories: [
      {
        id: 'document_risks',
        label: 'Na podstawie umowy wskaż ryzyka',
        promptTemplate: 'Na podstawie załączonej umowy wskaż ryzyka prawne.',
        docType: 'legal_opinion',
        role: 'business_advisor',
        instructions: 'Przeanalizuj dokument i zidentyfikuj ryzyka.',
      },
      {
        id: 'evaluate_argumentation',
        label: 'Oceń argumentację w piśmie',
        promptTemplate: 'Oceń argumentację prawną zawartą w załączonym piśmie.',
        docType: 'legal_opinion',
        role: 'neutral',
        instructions: 'Wskaż mocne i słabe strony argumentacji.',
      },
    ],
  },
  {
    id: 'legal_status',
    label: 'Stan prawny',
    icon: Clock,
    color: 'indigo',
    subcategories: [
      {
        id: 'current_legal_status',
        label: 'Wskazanie daty stanu prawnego',
        promptTemplate: 'Ustal aktualny stan prawny w danej kwestii ze wskazaniem daty.',
        docType: 'legal_opinion',
        role: 'neutral',
        instructions: 'Wskaż datę, według której ustalany jest stan prawny.',
      },
      {
        id: 'historical_legal_status',
        label: 'Stan prawny na wskazany dzień',
        promptTemplate: 'Odtwórz stan prawny obowiązujący na wskazaną datę.',
        docType: 'legal_opinion',
        role: 'neutral',
        instructions: 'Uwzględnij przepisy przejściowe i zmiany.',
      },
    ],
  },
  {
    id: 'research_other',
    label: 'INNE',
    icon: Search,
    color: 'gray',
    subcategories: [
      {
        id: 'other_research',
        label: 'Inny research prawny',
        promptTemplate: 'Przeprowadź research prawny we wskazanym zakresie.',
        docType: 'legal_opinion',
        role: 'neutral',
      },
    ],
  },
]

// ============================================================================
// COMBINED CONFIGURATION
// ============================================================================

export const SHORTCUTS_SECTIONS: ShortcutSection[] = [
  {
    id: 'document_generation',
    label: 'Generowanie dokumentów',
    categories: DOCUMENT_GENERATION_CATEGORIES,
  },
  {
    id: 'document_analysis',
    label: 'Analiza i review dokumentów',
    categories: DOCUMENT_ANALYSIS_CATEGORIES,
  },
  {
    id: 'legal_research',
    label: 'Research prawny',
    categories: LEGAL_RESEARCH_CATEGORIES,
  },
]

// Flat list of all categories for backward compatibility
export const SHORTCUTS_CONFIG: ShortcutCategory[] = [
  ...DOCUMENT_GENERATION_CATEGORIES,
  ...DOCUMENT_ANALYSIS_CATEGORIES,
  ...LEGAL_RESEARCH_CATEGORIES,
]

// Helper to find a shortcut by category and subcategory IDs
export function findShortcut(categoryId: string, subcategoryId: string): ShortcutSubcategory | null {
  const category = SHORTCUTS_CONFIG.find(c => c.id === categoryId)
  if (!category) return null
  return category.subcategories.find(s => s.id === subcategoryId) || null
}

// Helper to get Tailwind classes for a color
export function getColorClasses(color: string): { bg: string; bgHover: string; text: string; border: string; bgLight: string } {
  const colorMap: Record<string, { bg: string; bgHover: string; text: string; border: string; bgLight: string }> = {
    blue: { bg: 'bg-blue-500', bgHover: 'hover:bg-blue-600', text: 'text-blue-600', border: 'border-blue-200', bgLight: 'bg-blue-50' },
    red: { bg: 'bg-red-500', bgHover: 'hover:bg-red-600', text: 'text-red-600', border: 'border-red-200', bgLight: 'bg-red-50' },
    purple: { bg: 'bg-purple-500', bgHover: 'hover:bg-purple-600', text: 'text-purple-600', border: 'border-purple-200', bgLight: 'bg-purple-50' },
    green: { bg: 'bg-green-500', bgHover: 'hover:bg-green-600', text: 'text-green-600', border: 'border-green-200', bgLight: 'bg-green-50' },
    amber: { bg: 'bg-amber-500', bgHover: 'hover:bg-amber-600', text: 'text-amber-600', border: 'border-amber-200', bgLight: 'bg-amber-50' },
    indigo: { bg: 'bg-indigo-500', bgHover: 'hover:bg-indigo-600', text: 'text-indigo-600', border: 'border-indigo-200', bgLight: 'bg-indigo-50' },
    orange: { bg: 'bg-orange-500', bgHover: 'hover:bg-orange-600', text: 'text-orange-600', border: 'border-orange-200', bgLight: 'bg-orange-50' },
    teal: { bg: 'bg-teal-500', bgHover: 'hover:bg-teal-600', text: 'text-teal-600', border: 'border-teal-200', bgLight: 'bg-teal-50' },
    sky: { bg: 'bg-sky-500', bgHover: 'hover:bg-sky-600', text: 'text-sky-600', border: 'border-sky-200', bgLight: 'bg-sky-50' },
    emerald: { bg: 'bg-emerald-500', bgHover: 'hover:bg-emerald-600', text: 'text-emerald-600', border: 'border-emerald-200', bgLight: 'bg-emerald-50' },
    slate: { bg: 'bg-slate-500', bgHover: 'hover:bg-slate-600', text: 'text-slate-600', border: 'border-slate-200', bgLight: 'bg-slate-50' },
    cyan: { bg: 'bg-cyan-500', bgHover: 'hover:bg-cyan-600', text: 'text-cyan-600', border: 'border-cyan-200', bgLight: 'bg-cyan-50' },
    rose: { bg: 'bg-rose-500', bgHover: 'hover:bg-rose-600', text: 'text-rose-600', border: 'border-rose-200', bgLight: 'bg-rose-50' },
    violet: { bg: 'bg-violet-500', bgHover: 'hover:bg-violet-600', text: 'text-violet-600', border: 'border-violet-200', bgLight: 'bg-violet-50' },
    pink: { bg: 'bg-pink-500', bgHover: 'hover:bg-pink-600', text: 'text-pink-600', border: 'border-pink-200', bgLight: 'bg-pink-50' },
    fuchsia: { bg: 'bg-fuchsia-500', bgHover: 'hover:bg-fuchsia-600', text: 'text-fuchsia-600', border: 'border-fuchsia-200', bgLight: 'bg-fuchsia-50' },
    lime: { bg: 'bg-lime-500', bgHover: 'hover:bg-lime-600', text: 'text-lime-600', border: 'border-lime-200', bgLight: 'bg-lime-50' },
    gray: { bg: 'bg-gray-500', bgHover: 'hover:bg-gray-600', text: 'text-gray-600', border: 'border-gray-200', bgLight: 'bg-gray-50' },
  }
  return colorMap[color] || colorMap.blue
}
